import { Shirt, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { TreasureReward } from "@/lib/utils/treasure";

const ASSET_PATH = {
  fox: "/treasure/accessories/fox.webp?v=1",
  cat: "/treasure/accessories/cat.webp?v=1",
  dog: "/treasure/accessories/dog.webp?v=1",
  chicken: "/treasure/accessories/chicken.webp?v=1",
  tiger: "/treasure/accessories/tiger.webp?v=1",
  backpack: "/treasure/accessories/backpack.webp?v=1",
  "school-backpack": "/treasure/accessories/school-backpack.webp?v=1",
  "weekend-backpack": "/treasure/accessories/weekend-backpack.webp?v=1",
  "hiking-backpack": "/treasure/accessories/hiking-backpack.webp?v=1",
  "travel-backpack": "/treasure/accessories/travel-backpack.webp?v=1",
  glasses: "/treasure/accessories/glasses.webp?v=1",
  sunglasses: "/treasure/accessories/sunglasses.webp?v=1",
  "red-sunglasses": "/treasure/accessories/red-sunglasses.webp?v=1",
  "mirror-glasses": "/treasure/accessories/mirror-glasses.webp?v=1",
  "cat-eye-glasses": "/treasure/accessories/cat-eye-glasses.webp?v=1",
  "dollar-glasses": "/treasure/accessories/dollar-glasses.webp?v=1",
  chain: "/treasure/accessories/chain.webp?v=1",
  hair: "/treasure/accessories/hair.webp?v=1",
} as const;

const VARIANT_CLASS: Record<TreasureReward["variant"], string> = {
  original: "",
  sunny: "treasure-variant-sunny",
  berry: "treasure-variant-berry",
  night: "treasure-variant-night",
  sky: "treasure-variant-sky",
  meadow: "treasure-variant-meadow",
  red: "",
  black: "",
  orange: "",
  mirror: "",
};

const HOODIE_CLASS: Record<TreasureReward["variant"], string> = {
  original: "bg-[#27854D]",
  sunny: "bg-[#F5B942]",
  berry: "bg-[#8B5AD9]",
  night: "bg-[#334766]",
  sky: "bg-[#4EA6D8]",
  meadow: "bg-[#27854D]",
  red: "bg-[#C93F42]",
  black: "bg-[#222936]",
  orange: "bg-[#E7792E]",
  mirror: "bg-[#6CBEDB]",
};

export function TreasureRewardIcon({
  reward,
  className,
}: {
  reward: TreasureReward;
  className?: string;
}) {
  if (reward.asset === "scene") {
    return (
      <span
        className={cn("overflow-hidden rounded-[24%]", className)}
        aria-hidden
      >
        <img
          src={reward.scenePath}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
        />
      </span>
    );
  }

  if (reward.asset === "aura") {
    return (
      <span
        className={cn(
          "grid place-items-center rounded-full text-white shadow-inner",
          reward.variant === "meadow"
            ? "bg-[radial-gradient(circle,#76B852,#2D7A46)]"
            : reward.variant === "night"
              ? "bg-[radial-gradient(circle,#A58AF5,#4A3B8F)]"
              : "bg-[conic-gradient(#F7C948,#62C6E8,#A58AF5,#F68A9B,#F7C948)]",
          className,
        )}
        aria-hidden
      >
        <Sparkles className="h-[62%] w-[62%]" strokeWidth={2.4} />
      </span>
    );
  }

  if (reward.asset === "hoodie") {
    return (
      <span
        className={cn(
          "grid place-items-center rounded-[24%] text-white shadow-inner",
          HOODIE_CLASS[reward.variant],
          className,
        )}
        aria-hidden
      >
        <Shirt className="h-[62%] w-[62%]" strokeWidth={2.4} />
      </span>
    );
  }

  return (
    <img
      src={ASSET_PATH[reward.asset]}
      alt=""
      className={cn(
        "select-none object-contain drop-shadow-[0_4px_5px_rgba(34,52,40,0.16)]",
        treasureVariantClass(reward),
        className,
      )}
      draggable={false}
    />
  );
}

export function treasureVariantClass(reward: TreasureReward): string {
  if (reward.asset === "hair" && reward.variant === "berry") {
    return "treasure-hair-berry";
  }
  if (reward.asset === "hair" && reward.variant === "sunny") {
    return "treasure-hair-sunny";
  }
  if (reward.asset === "chain" && reward.variant === "sky") {
    return "treasure-chain-silver";
  }
  if (reward.asset === "chain" && reward.variant === "meadow") {
    return "treasure-chain-meadow";
  }
  return VARIANT_CLASS[reward.variant];
}

export function treasureAssetPath(reward: TreasureReward): string | undefined {
  if (
    reward.asset === "aura" ||
    reward.asset === "hoodie" ||
    reward.asset === "scene"
  ) {
    return reward.asset === "scene" ? reward.scenePath : undefined;
  }
  return ASSET_PATH[reward.asset];
}
